/**
 * UserMenuDropdown — dropdown utente in AppTopBar (desktop).
 *
 * Implementato a mano (no Radix Popover per evitare nuova dep):
 * - click sul trigger toggle dropdown
 * - click outside chiude
 * - tasto Esc chiude
 * - focus trap minimo (manda focus al primo item all'apertura)
 *
 * Items:
 * - Profilo (placeholder, disabilitato per PR3)
 * - Esci — usa useLogoutConfirm (stessa logica di logo AppSidebar e
 *   logo AppTopBar): apre LogoutConfirmDialog, poi signOut + /login.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLogoutConfirm } from '../../hooks/useLogoutConfirm';
import { Avatar } from '../ui/Avatar';
import { Icon } from '@shared/components/ui/Icon';
import { LogoutConfirmDialog } from './LogoutConfirmDialog';

export function UserMenuDropdown() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const { confirmOpen, triggerLogout, confirmLogout, cancelLogout } =
    useLogoutConfirm();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    // Focus al primo item dopo open
    queueMicrotask(() => firstItemRef.current?.focus());
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleEsci = useCallback(() => {
    close();
    triggerLogout();
  }, [close, triggerLogout]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu utente"
        className="flex items-center gap-2 h-11 pl-1 pr-2 rounded-full hover:bg-bg-app transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-magenta-soft"
      >
        <Avatar
          name={user?.displayName}
          email={user?.email}
          size="md"
        />
        <Icon
          name="expand_more"
          size={20}
          className="text-text-secondary"
        />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Menu utente"
          className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-border-gray py-2 z-50"
        >
          <div className="px-4 py-2 border-b border-border-gray">
            <p className="font-body-sm font-semibold text-text-main truncate">
              {user?.displayName ?? 'Utente'}
            </p>
            {user?.email && (
              <p className="text-[12px] text-text-secondary truncate">
                {user.email}
              </p>
            )}
          </div>
          <button
            ref={firstItemRef}
            type="button"
            role="menuitem"
            disabled
            aria-disabled
            title="Disponibile in una prossima versione"
            className="w-full flex items-center gap-2 px-4 py-2 text-body-md text-text-secondary opacity-60 cursor-not-allowed text-left"
          >
            <Icon name="person" size={18} />
            Profilo
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleEsci}
            className="w-full flex items-center gap-2 px-4 py-2 text-body-md text-text-main hover:bg-bg-app text-left focus:outline-none focus:bg-bg-app"
          >
            <Icon name="logout" size={18} />
            Esci
          </button>
        </div>
      )}
      <LogoutConfirmDialog
        open={confirmOpen}
        onConfirm={() => void confirmLogout()}
        onCancel={cancelLogout}
      />
    </div>
  );
}
